// src/contracts/components/ExhibitsPanel.js
// Fluid exhibits manager. Letters (A, B, C, ...) are derived from order in the
// array. Supports: drag-to-reorder (grip handle, gap-opening drop indicator),
// delete, rename, add new (typed), upload PDF via drag/drop or picker, and
// paste-from-Sheets TSV into the Material List table inline. Per-exhibit info
// shows on hover over the letter badge to keep the cards uncluttered.

import React, { useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { GripVertical, Plus, Trash2, Upload, Pencil, Check } from 'lucide-react';

import ContractTableEditor from './ContractTableEditor';
import api from '../api';

function letterOf(i) { return String.fromCharCode(65 + i); }
const uid = (prefix = 'ex') => `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;

const KIND_OPTIONS = [
  { kind: 'gli',          title: 'General Liability Insurance' },
  { kind: 'wc',           title: "Workers' Compensation Insurance" },
  { kind: 'cancel_3day',  title: '3 day right to cancel' },
  { kind: 'cancel_7day',  title: '7 day right to cancel' },
  { kind: 'change_order', title: 'Sample change order form' },
  { kind: 'material_list',title: 'Material List' },
  { kind: 'custom',       title: 'Custom exhibit' },
];

// Short description surfaced as a tooltip on the letter badge — replaces the
// inline helper text and acronym tag that used to clutter each card.
const KIND_INFO = {
  gli:          'General Liability Insurance — pulled from Settings → Company',
  wc:           "Workers' Compensation Insurance — pulled from Settings → Company",
  cancel_3day:  '3-day right to cancel notice',
  cancel_7day:  '7-day right to cancel notice',
  change_order: 'Sample change order form',
  material_list:'Material List — paste a range from Google Sheets',
  custom:       'Custom exhibit — free text paragraphs',
  uploaded_pdf: 'Uploaded PDF — merged as native pages',
};
function infoFor(ex) {
  if (ex.kind === 'uploaded_pdf') return ex.originalFileName || KIND_INFO.uploaded_pdf;
  return KIND_INFO[ex.kind] || ex.kind;
}

function ExhibitCard({ ex, index, onDelete, onChange, onUploadPdf, onGripDown }) {
  const [renaming, setRenaming] = useState(false);
  const [tempTitle, setTempTitle] = useState(ex.title || '');
  const fileRef = useRef(null);

  const letter = letterOf(index);

  const handlePdfPicker = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    await onUploadPdf(file);
  };

  const handleSheetsPaste = (e) => {
    const text = (e.clipboardData || window.clipboardData).getData('text');
    if (!text || !text.includes('\t')) return; // not TSV
    e.preventDefault();
    const lines = text.split(/\r?\n/).filter(Boolean);
    const rows = lines.map(l => l.split('\t'));
    const ncols = Math.max(...rows.map(r => r.length));
    const columns = (ex.data?.table?.columns?.length === ncols)
      ? ex.data.table.columns
      : Array.from({ length: ncols }).map((_, i) => ({ header: `Col ${i + 1}`, width: 1 }));
    onChange({ ...ex, data: { ...ex.data, table: { columns, rows } } });
  };

  // Only kinds with editable/visible content render a body; standard exhibits
  // (insurance, cancel notices, change order) carry no inline content.
  const hasBody = ex.kind === 'uploaded_pdf' || ex.kind === 'material_list' || ex.kind === 'custom';

  return (
    <div className="rounded-xl border border-zinc-800 bg-gray-900/50 overflow-hidden">
      <div
        className="flex items-center gap-2 px-3 py-2 bg-gray-950/40 border-b border-zinc-800 select-none cursor-grab active:cursor-grabbing"
        style={{ touchAction: 'none' }}
        onPointerDown={(e) => { if (e.target.closest('button, input')) return; onGripDown(e); }}
        title="Drag to reorder"
      >
        <GripVertical className="w-4 h-4 text-zinc-500 shrink-0" aria-hidden />
        <span
          className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-indigo-950/60 text-indigo-200 text-xs font-medium cursor-help"
          title={infoFor(ex)}
        >
          {letter}
        </span>
        <div className="flex-1 min-w-0">
          {renaming ? (
            <div className="flex items-center gap-2">
              <input
                value={tempTitle}
                onChange={e => setTempTitle(e.target.value)}
                className="flex-1 bg-gray-950 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-100 select-text"
                autoFocus
              />
              <button
                type="button"
                onClick={() => { onChange({ ...ex, title: tempTitle }); setRenaming(false); }}
                className="text-emerald-300 hover:text-emerald-200 p-1"
                aria-label="Save title"
              >
                <Check className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm text-zinc-100 truncate">{ex.title || '(untitled)'}</span>
              <button
                type="button"
                onClick={() => { setTempTitle(ex.title || ''); setRenaming(true); }}
                className="text-zinc-500 hover:text-indigo-300 p-0.5"
                aria-label="Rename"
              >
                <Pencil className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onDelete}
          className="p-1 text-zinc-500 hover:text-red-300"
          aria-label="Delete exhibit"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {hasBody && (
        <div className="p-3">
          {ex.kind === 'uploaded_pdf' ? (
            <div className="text-xs text-zinc-300">
              <div className="flex items-center gap-2">
                <Upload className="w-3.5 h-3.5 text-indigo-300" />
                <span className="truncate">{ex.originalFileName || ex.title}</span>
                {ex.pageCount ? <span className="text-zinc-500">· {ex.pageCount} {ex.pageCount === 1 ? 'page' : 'pages'}</span> : null}
              </div>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="mt-2 text-[11px] text-indigo-300 hover:text-indigo-200"
              >
                Replace file…
              </button>
              <input ref={fileRef} type="file" accept="application/pdf" onChange={handlePdfPicker} className="hidden" />
            </div>
          ) : ex.kind === 'material_list' ? (
            <div onPaste={handleSheetsPaste}>
              <ContractTableEditor
                compact
                value={ex.data?.table || { columns: [], rows: [] }}
                onChange={(table) => onChange({ ...ex, data: { ...ex.data, table } })}
              />
            </div>
          ) : (
            <textarea
              value={(ex.data?.paragraphs || []).join('\n\n')}
              onChange={(e) => onChange({ ...ex, data: { ...ex.data, paragraphs: e.target.value.split(/\n{2,}/) } })}
              rows={4}
              placeholder="Add content for this custom exhibit (paragraphs separated by blank lines)"
              className="w-full bg-gray-950 border border-zinc-800 rounded px-3 py-2 text-[13px] text-zinc-100 leading-relaxed"
            />
          )}
        </div>
      )}
    </div>
  );
}

export default function ExhibitsPanel({ exhibits, contractId, onChangeExhibits }) {
  const [addOpen, setAddOpen] = useState(false);

  // ---- Drag-to-reorder: custom POINTER drag (floating copy + dashed bar, no snap-back) ----
  const [drag, setDrag] = useState(null);       // { fromIndex, w, h, dx, dy } while dragging
  const [pointer, setPointer] = useState(null); // { x, y } current cursor position
  const [dropIndex, setDropIndex] = useState(null);
  const rowRefs = useRef([]);
  const dragRef = useRef(null);
  const moveExhibitToIndex = (from, insertIndex) => {
    if (from == null || insertIndex == null) return;
    if (insertIndex === from || insertIndex === from + 1) return; // no-op gap
    const next = exhibits.slice();
    const [moved] = next.splice(from, 1);
    const dest = from < insertIndex ? insertIndex - 1 : insertIndex;
    next.splice(dest, 0, moved);
    onChangeExhibits(next);
  };
  const computeDropIndex = (clientY, fromIndex) => {
    for (let i = 0; i < exhibits.length; i++) {
      if (i === fromIndex) continue;
      const el = rowRefs.current[i];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (clientY < r.top + r.height / 2) return i;
    }
    return exhibits.length;
  };
  const startExhibitDrag = (e, index) => {
    e.preventDefault();
    const el = rowRefs.current[index];
    if (!el) return;
    const r = el.getBoundingClientRect();
    dragRef.current = { fromIndex: index, dropIndex: index };
    setDrag({ fromIndex: index, w: r.width, h: r.height, dx: e.clientX - r.left, dy: e.clientY - r.top });
    setPointer({ x: e.clientX, y: e.clientY });
    setDropIndex(index);
    const onMove = (ev) => {
      setPointer({ x: ev.clientX, y: ev.clientY });
      const idx = computeDropIndex(ev.clientY, index);
      dragRef.current.dropIndex = idx;
      setDropIndex(idx);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      const d = dragRef.current;
      if (d) moveExhibitToIndex(d.fromIndex, d.dropIndex);
      dragRef.current = null;
      setDrag(null); setPointer(null); setDropIndex(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const onDelete = (i) => {
    const next = exhibits.slice();
    next.splice(i, 1);
    onChangeExhibits(next);
  };
  const onChangeOne = (i, ex) => {
    const next = exhibits.slice();
    next[i] = ex;
    onChangeExhibits(next);
  };

  const addExhibit = (kind, title) => {
    const ex = { exhibitId: uid('ex'), kind, title: title || kind, data: {} };
    if (kind === 'material_list') {
      ex.data = { table: { columns: [{ header: 'Item', width: 4 }, { header: 'Total', width: 1 }, { header: 'Make', width: 2 }, { header: 'Model', width: 3 }], rows: [['', '', '', '']] } };
    }
    onChangeExhibits([...exhibits, ex]);
    setAddOpen(false);
  };

  const handleUploaded = async (file, targetExhibitId = null) => {
    if (!contractId) {
      alert('Please save the draft first (auto-save will kick in after one edit).');
      return;
    }
    const exhibitId = targetExhibitId || uid('ex');
    try {
      const res = await api.uploadExhibit(contractId, exhibitId, file);
      const newExhibit = {
        exhibitId,
        kind: 'uploaded_pdf',
        title: file.name,
        originalFileName: res.originalFileName,
        pageCount: res.pageCount,
        tempPath: res.tempPath,
        data: {},
      };
      if (targetExhibitId) {
        const idx = exhibits.findIndex(x => x.exhibitId === targetExhibitId);
        if (idx >= 0) {
          const next = exhibits.slice();
          next[idx] = { ...next[idx], ...newExhibit, exhibitId: targetExhibitId };
          onChangeExhibits(next);
          return;
        }
      }
      onChangeExhibits([...exhibits, newExhibit]);
    } catch (err) {
      console.error('Exhibit upload failed', err);
      alert('Exhibit upload failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true,
    onDrop: async (files) => {
      for (const f of files) await handleUploaded(f);
    },
  });

  const dropSpacer = (key) => (
    <div key={key} className="h-9 rounded-lg border-2 border-dashed border-indigo-400 bg-indigo-500/15 pointer-events-none" />
  );

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3">
      <div className="shrink-0 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Exhibits</h2>
        <div className="relative">
          <button
            type="button"
            onClick={() => setAddOpen(o => !o)}
            className="px-3 py-1.5 text-xs text-zinc-200 border border-zinc-700 hover:bg-zinc-800 rounded-lg inline-flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" /> add
          </button>
          {addOpen && (
            <div className="absolute top-9 right-0 bg-zinc-900 border border-zinc-700 rounded-md py-1 z-30 min-w-[230px]">
              {KIND_OPTIONS.map(o => (
                <button
                  key={o.kind}
                  type="button"
                  onClick={() => addExhibit(o.kind, o.title)}
                  className="w-full text-left px-3 py-1.5 text-xs text-zinc-200 hover:bg-zinc-800"
                >
                  {o.title}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto space-y-2 pr-1">
        {exhibits.map((ex, i) => {
          const isDragging = drag && drag.fromIndex === i;
          const showGapBefore = drag && dropIndex === i
            && dropIndex !== drag.fromIndex && dropIndex !== drag.fromIndex + 1;
          return (
            <React.Fragment key={ex.exhibitId}>
              {showGapBefore && dropSpacer(`gap-${i}`)}
              <div ref={(el) => (rowRefs.current[i] = el)} className={isDragging ? 'invisible' : ''}>
                <ExhibitCard
                  ex={ex}
                  index={i}
                  onDelete={() => onDelete(i)}
                  onChange={(next) => onChangeOne(i, next)}
                  onUploadPdf={(file) => handleUploaded(file, ex.exhibitId)}
                  onGripDown={(e) => startExhibitDrag(e, i)}
                />
              </div>
            </React.Fragment>
          );
        })}
        {drag && dropIndex === exhibits.length
          && dropIndex !== drag.fromIndex && dropIndex !== drag.fromIndex + 1 && dropSpacer('gap-end')}
      </div>

      {/* Floating copy of the card header that follows the cursor (no native ghost = no snap-back) */}
      {drag && pointer && (() => {
        const ex = exhibits[drag.fromIndex] || {};
        return (
          <div
            className="fixed z-[1000] pointer-events-none rounded-xl border border-zinc-700 bg-gray-900 shadow-2xl overflow-hidden"
            style={{ left: pointer.x - drag.dx, top: pointer.y - drag.dy, width: drag.w }}
          >
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-950/70">
              <GripVertical className="w-4 h-4 text-zinc-500 shrink-0" aria-hidden />
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-indigo-950/60 text-indigo-200 text-xs font-medium">{letterOf(drag.fromIndex)}</span>
              <span className="text-sm text-zinc-100 truncate">{ex.title || '(untitled)'}</span>
            </div>
          </div>
        );
      })()}

      <div
        {...getRootProps()}
        className={`shrink-0 rounded-xl border-2 border-dashed p-5 text-center cursor-pointer transition-colors ${
          isDragActive ? 'border-indigo-500 bg-indigo-950/20' : 'border-zinc-800 hover:border-zinc-700 bg-gray-950/30'
        }`}
      >
        <input {...getInputProps()} />
        <Upload className="w-5 h-5 mx-auto text-zinc-500" aria-hidden />
        <div className="mt-1 text-xs text-zinc-400">
          {isDragActive ? 'drop the PDF…' : 'drag a PDF here — becomes the next exhibit'}
        </div>
      </div>
    </div>
  );
}
